import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import BannerService from "../../Services/BannerService";

export default function AddBanner({
  show,
  onHide,
  onSuccess,
  pageTitle = "Banner",
}) {
  const imageRef = useRef(null);
  const [bannerData, setBannerData] = useState({
    image: "",
    title: "",
    description: "",
    link: "",
  });
  const [validated, setValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleImageInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG)");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setBannerData((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setBannerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (
      !bannerData.title.trim() ||
      !bannerData.description.trim() ||
      !bannerData.image
    ) {
      event.stopPropagation();
      return;
    }

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Creating...");

      const formData = new FormData();

      formData.append("image", bannerData.image);

      const processedTitle = bannerData.title.trim().replace(/\r?\n/g, "\\n");
      const processedDescription = bannerData.description
        .trim()
        .replace(/\r?\n/g, "\\n");
      formData.append("title", processedTitle);
      formData.append("description", processedDescription);

      const response = await BannerService.createBanner(formData);

      if (response.data.success) {
        toast.success(response.data.message, { id: loadingMessage });
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(response.data.message || "Something went wrong", {
          id: loadingMessage,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setValidated(false);
    setBannerData({
      image: "",
      title: "",
      description: "",
      link: "",
    });
    onHide();
  };

  const handleRemoveNewImage = () => {
    setBannerData((prev) => ({
      ...prev,
      image: "",
    }));
    // Reset the file input
    if (imageRef.current) {
      imageRef.current.value = "";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="lg">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          <i className="bi bi-image me-2"></i>
          Add {pageTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            {/* Title */}
            <Col xs={12} md={6}>
              <Form.Group controlId="title">
                <Form.Label className="form-label fs-5">Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={bannerData.title}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                  required
                  isInvalid={validated && !bannerData.title.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a title
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/*  Link */}
            {/* <Col xs={12} md={6}>
              <Form.Group controlId="link">
                <Form.Label className="form-label fs-5">
                  Link (Optional)
                </Form.Label>
                <Form.Control
                  type="url"
                  name="link"
                  value={bannerData.link}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="form-control fs-5"
                />
              </Form.Group>
            </Col>  */}

            {/* Description */}
            <Col xs={12} md={12}>
              <Form.Group controlId="description">
                <Form.Label className="form-label fs-5">
                  Description *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={bannerData.description}
                  onChange={handleInputChange}
                  placeholder="Enter banner description"
                  required
                  isInvalid={validated && !bannerData.description.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Image */}
            <Col xs={12} md={12}>
              <Form.Label className="form-label fs-5">
                Banner Image *
              </Form.Label>

              {/* Show new image preview */}
              {bannerData.image ? (
                <div className="mt-2">
                  <p className="text-muted small mb-2">New Image:</p>
                  <div className="d-flex position-relative">
                    <img
                      src={URL.createObjectURL(bannerData.image)}
                      alt="New Upload"
                      style={{ maxWidth: "90%", maxHeight: "200px" }}
                      className="img-fluid"
                    />
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{
                        position: "absolute",
                        right: "35px",
                        top: "-13px",
                      }}
                      onClick={handleRemoveNewImage}
                    >
                      <i className="bi bi-x-circle-fill text-danger"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label
                    htmlFor="importInput_image"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-cloud-plus me-2"></i> Upload Image
                  </label>
                  <input
                    id="importInput_image"
                    type="file"
                    className="d-none form-control fs-5"
                    ref={imageRef}
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageInput}
                    required
                  />
                  {validated && !bannerData.image && (
                    <div className="invalid-feedback d-block">
                      Please select an image file (JPG, PNG)
                    </div>
                  )}
                </div>
              )}
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Create Banner
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
